import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/healthy-people-carrying-different-icons_53876-66139.jpg';

function PublicHealthInitiatives() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match reference layout)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Apply for Benefits with the One DHS Portal',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Create or log in:</span>
              <span style={valText}>
                Visit OneDhs.GTS.Gov and sign in. Guests can start an application, but can’t save progress. You’ll have 48 hours to complete the application once started.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Choose programs:</span>
              <span style={valText}>
                From the dashboard, select <em>Apply for Child Care and/or Family Assistance</em>. Pick SNAP, Families First (TANF), and/or Child Care Payment Assistance, then proceed through each page and review summaries before submitting.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Submit & save your number:</span>
              <span style={valText}>
                After signing and submitting, you’ll receive an application number. Save it; you’ll need it for follow-up and document upload.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Upload Verification Documents (Next Step)',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Link your case:</span>
              <span style={valText}>
                In the portal, choose <em>Link SNAP/Families First Case</em>, then open the <em>Child Care & Family Assistance File Upload</em> tile. Select the correct case/application number from the dropdown to avoid delays.
              </span>
            </div>
          </li>
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Choose type & submit:</span>
              <span style={valText}>
                Pick the person and document type, upload files (or use your phone camera), and click <em>Submit</em>. You can view everything under <em>My Submissions</em>. Upload verifications within 10 days to keep your case moving.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Programs You Can Apply For',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['SNAP (Food Assistance):', 'Helps households buy food while they work toward self-sufficiency.'],
            ['Families First (TANF):', 'Cash assistance with employment and supportive services.'],
            ['Child Care Payment Assistance:', 'Helps eligible families pay for child care while working or in training/education.'],
          ].map(([k, v], i) => (
            <li key={`prog-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Languages & Accessibility',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Supported languages:</span>
              <span style={valText}>
                English, Español, Arabic, and Soomaali are available on the application’s <em>Before You Begin</em> page. Collect required documents first for a faster experience.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Timeframes & Interviews',
      Answer: (
        <ul className="list-unstyled list-style">
          <li style={liRow}>
            <i className="fa-solid fa-circle-arrow-right" style={icon} />
            <div style={kv}>
              <span style={keyText}>Keep your case on track:</span>
              <span style={valText}>
                Upload verifications within 10 days after submitting your application. A DHS team member will typically contact you within 10 days regarding the required eligibility interview.
              </span>
            </div>
          </li>
        </ul>
      ),
    },
    {
      Que: 'Tips to Avoid Delays',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Match the right case:', 'Always pick the correct case/application number before uploading documents.'],
            ['Combine files if needed:', 'If you upload all verifications as one file, check the “I have uploaded all my verifications” option.'],
            ['Review summaries:', 'Fix errors on summary pages before submitting your application.'],
          ].map(([k, v], i) => (
            <li key={`tip-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Help & Contacts',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['One DHS Contact Center:', '833-772-1234'],
            ['Family Assistance Service Center:', '866-311-1234'],
          ].map(([k, v], i) => (
            <li key={`help-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}>
                <span style={keyText}>{k}</span>
                <span style={valText}>{v}</span>
              </div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={SubDeptImage} className="img-fluid" alt="Public Health Initiatives" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Public Health Initiatives</h4>

                {/* Overview (always visible, same color as title but smaller) */}
                <p style={overviewText}>
                  We strengthen community health through prevention and access—vaccinations, health education, and benefit navigation—
                  including clear, step-by-step support for SNAP, TANF, and Child Care through the One DHS Customer Portal.
                </p>

                {/* Download button BELOW overview, ABOVE other sections */}
                <div style={{ margin: '0 0 16px' }}>
                  <a
                    href="/guides/GTS One DHS Customer Portal Apply for Services 080825.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      fontSize: '14px',
                      color: '#e75934',
                      textDecoration: 'none',
                      border: '1px solid #e75934',
                      borderRadius: '6px',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'transparent',
                      width: 'fit-content'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e75934';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#e75934';
                    }}
                  >
                    <i className="fa-solid fa-file-pdf" style={{ marginRight: '8px', fontSize: '14px' }} />
                    Download One DHS Customer Portal Guide (PDF)
                  </a>
                </div>
              </div>

              {/* Accordion (expanded information) */}
              <div className="accordian-box">
                {sections.map((item, index) => (
                  <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                    <div className="accordian-title">
                      <h5>{item.Que}</h5>
                      <i
                        className="fa-solid fa-angle-right"
                        onClick={() => setFaq(index)}
                        role="button"
                        aria-label={`Toggle ${item.Que}`}
                      />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: 'block' }}>
                        {item.Answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12 }}>
                <Link to="/department/health">
                  <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }}/>
                  Go Back
                </Link>
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default PublicHealthInitiatives;
